import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { NAV } from "@/constants/testIds";
import { SITE } from "@/lib/site";
import DroneLogo from "@/components/DroneLogo";

const LINKS = [
  { id: "services", label: "Services", testId: NAV.linkServices },
  { id: "pricing", label: "Tarifs", testId: NAV.linkPricing },
  { id: "mariage", label: "Mariage", testId: NAV.linkWedding },
  { id: "gallery", label: "Galerie", testId: "nav-link-gallery" },
  { id: "blog", label: "Blog", testId: "nav-link-blog", route: "/blog" },
  { id: "faq", label: "FAQ", testId: "nav-link-faq" },
  { id: "contact", label: "Contact", testId: NAV.linkContact },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id, route) => {
    setOpen(false);
    if (route) {
      navigate(route);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    // If not on home, navigate first, then let the hash-scroll effect run
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <motion.header
      data-testid={NAV.root}
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "py-3" : "py-5"
      }`}
    >
      <div className="mx-auto max-w-7xl px-6">
        <div
          className={`flex items-center justify-between rounded-full px-4 md:px-6 py-3 transition-all duration-300 ${
            scrolled ? "glass-strong shadow-[0_8px_32px_rgba(0,0,0,0.6)]" : "bg-transparent"
          }`}
        >
          <button
            data-testid={NAV.logo}
            onClick={() => {
              setOpen(false);
              if (location.pathname !== "/") navigate("/");
              else window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className="flex items-center gap-2.5 group"
          >
            <span className="relative inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#007AFF]/15 border border-[#007AFF]/40 text-[#007AFF]">
              <span className="absolute inset-0 rounded-full bg-[#007AFF] blur-md opacity-25 group-hover:opacity-50 transition" />
              <DroneLogo size={22} className="relative" />
            </span>
            <span className="font-display text-lg md:text-xl font-semibold tracking-tight">
              drones<span className="text-[#007AFF]">3x</span>.fr
            </span>
          </button>

          <nav className="hidden lg:flex items-center gap-1">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={l.testId}
                onClick={() => scrollTo(l.id, l.route)}
                className="px-4 py-2 text-sm text-white/70 hover:text-white transition rounded-full hover:bg-white/5"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <a
              data-testid={NAV.ctaCall}
              href={SITE.phoneLink}
              className="hidden md:inline-flex items-center gap-2 px-4 py-2 text-sm text-white/80 hover:text-white transition"
            >
              <Phone size={14} /> {SITE.phoneDisplay}
            </a>
            <button
              data-testid={NAV.ctaContact}
              onClick={() => scrollTo("contact")}
              className="hidden md:inline-flex items-center gap-2 rounded-full bg-[#007AFF] px-5 py-2.5 text-sm font-semibold text-white hover:shadow-[0_0_24px_rgba(0,122,255,0.55)] transition-all"
            >
              Devis gratuit
            </button>
            <button
              data-testid={NAV.mobileToggle}
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-full glass"
              aria-label="menu"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="lg:hidden mt-3 glass-strong rounded-2xl p-4"
            >
              <div className="flex flex-col">
                {LINKS.map((l) => (
                  <button
                    key={l.id}
                    onClick={() => scrollTo(l.id, l.route)}
                    className="text-left px-3 py-3 text-white/80 hover:text-white border-b border-white/5 last:border-0"
                  >
                    {l.label}
                  </button>
                ))}
                <a
                  href={SITE.phoneLink}
                  className="mt-3 inline-flex items-center justify-center gap-2 rounded-full bg-[#007AFF] px-5 py-3 text-sm font-semibold text-white"
                >
                  <Phone size={14} /> {SITE.phoneDisplay}
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};

export default Navbar;
